import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Crosshair, MapPinned, Plus, Wine, Utensils, Store, Compass,
} from "lucide-react";
import { api, formatApiError, STAGES, stageMeta } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/UI";

// Mapbox GL JS is loaded as a global from the CDN <script> tag in index.html.
// This is intentional — see the comment in index.html. We read `window.mapboxgl`
// instead of `import mapboxgl from "mapbox-gl"` so the version ships exactly
// as 3.8.0 from the CDN regardless of any bundler/dependency cache behaviour.
const mapboxgl = typeof window !== "undefined" ? window.mapboxgl : null;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (mapboxgl) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

const RADII = [
  { mi: 3, label: "3 mi" },
  { mi: 5, label: "5 mi" },
  { mi: 10, label: "10 mi" },
  { mi: 25, label: "25 mi" },
];

const KM_PER_MILE = 1.609344;

const CATEGORIES = [
  { value: "restaurant", label: "Restaurants", icon: Utensils },
  { value: "bar", label: "Bars", icon: Wine },
  { value: "liquor_store", label: "Liquor Stores", icon: Store },
];

const STAGE_DOT = {
  lead: "#94A3B8",
  qualified: "#EAB308",
  demo: "#F97316",
  proposal: "#6366F1",
  closed: "#16A34A",
  lost: "#DC2626",
};

function parseAddress(full) {
  if (!full) return { address: "", city: "", state: "", zip_code: "" };
  const parts = full.split(",").map((s) => s.trim());
  const address = parts[0] || "";
  const city = parts[1] || "";
  let state = "", zip = "";
  if (parts[2]) {
    const m = parts[2].match(/^([A-Za-z .]+)\s+(\d{4,6}.*)$/);
    if (m) { state = m[1].trim(); zip = m[2].trim(); }
    else state = parts[2];
  }
  if (!zip && parts[3]) zip = parts[3];
  return { address, city, state, zip_code: zip };
}

function haversineMi(a, b) {
  const R = 3958.7613; // Earth radius in miles
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function htmlEscape(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Reusable colored pin DOM element for mapbox markers
function makePin({ color = "#1A1A1A", ring = "#FFFFFF", size = 22, ariaLabel = "marker" }) {
  const el = document.createElement("div");
  el.setAttribute("aria-label", ariaLabel);
  el.style.cssText = `
    width:${size}px;height:${size}px;border-radius:9999px;
    background:${color};border:3px solid ${ring};
    box-shadow:0 2px 8px rgba(0,0,0,0.25);cursor:pointer;
    transition:transform .15s ease;
  `;
  el.onmouseenter = () => { el.style.transform = "scale(1.15)"; };
  el.onmouseleave = () => { el.style.transform = "scale(1)"; };
  return el;
}

export default function Search() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const resultMarkersRef = useRef([]);
  const myEstabMarkersRef = useRef([]);
  const popupRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [loc, setLoc] = useState(null);
  const [locLabel, setLocLabel] = useState("");
  const [locInput, setLocInput] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [radius, setRadius] = useState(10);
  const [category, setCategory] = useState("restaurant");
  const [results, setResults] = useState([]);
  const [myEstabs, setMyEstabs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [importedIds, setImportedIds] = useState(new Set());
  const [activeResultId, setActiveResultId] = useState(null);

  const [mapError, setMapError] = useState("");

  // Initialize the map once
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    // Mapbox loaded from CDN script — bail gracefully if it never arrived.
    if (!mapboxgl) {
      setMapError(
        "Map library failed to load. Check your internet connection and refresh."
      );
      return;
    }
    // No token configured → loud error so the admin knows what to fix.
    if (!MAPBOX_TOKEN || MAPBOX_TOKEN.length < 10) {
      setMapError(
        "Mapbox token is not configured for this deployment. Ask your admin to set REACT_APP_MAPBOX_TOKEN."
      );
      return;
    }
    // `mapboxgl.supported()` was REMOVED in v3 — fall back to a manual WebGL2 probe.
    const _check = (typeof mapboxgl.supported === "function")
      ? mapboxgl.supported({ failIfMajorPerformanceCaveat: false })
      : (() => {
          try {
            const c = document.createElement("canvas");
            return !!(c.getContext("webgl2") || c.getContext("experimental-webgl2"));
          } catch (_) { return false; }
        })();
    if (!_check) {
      setMapError(
        "This browser doesn't support the interactive map. Try Safari/Chrome on iOS 15+, or use the list below."
      );
      return;
    }
    try {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: [-84.388, 33.749], // Atlanta default — visible even before geolocation
        zoom: 10,
        attributionControl: false,
        cooperativeGestures: false,
      });
      map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), "top-right");
      map.on("load", () => setMapReady(true));
      map.on("error", (e) => {
        const msg = e?.error?.message || "";
        if (/access ?token|401|403/i.test(msg)) {
          setMapError("Mapbox rejected the access token. Check the value of REACT_APP_MAPBOX_TOKEN.");
        } else if (/webgl|context|init/i.test(msg)) {
          setMapError("Map couldn't initialize. Refresh the page or try a different browser.");
        }
      });
      mapRef.current = map;
      return () => {
        // Mapbox's .remove() can throw during fast unmounts. Swallowing
        // here is intentional — the map ref is being torn down anyway.
        try { map.remove(); } catch (_) { /* unmount race; safe to ignore */ }
        mapRef.current = null;
      };
    } catch (err) {
      setMapError(
        "Map couldn't load on this device. You can still browse and import nearby places from the list below."
      );
    }
  }, []);

  // Load user's establishments once (with coords) to pin on map
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/establishments", { params: { limit: 500 } });
        const withCoords = (data.items || []).filter(
          (e) => e.latitude != null && e.longitude != null
        );
        setMyEstabs(withCoords);
      } catch (e) {
        // Pin loading is a visual enhancement — log but don't disrupt the
        // search UX if /establishments returns an error.
        console.warn("Could not load establishment pins for map:", e);
      }
    })();
  }, []);

  // Reverse geocode the current location → friendly city label.
  useEffect(() => {
    if (!loc) return;
    let cancelled = false;
    (async () => {
      try {
        const url =
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${loc.lng},${loc.lat}.json` +
          `?types=place,locality,neighborhood&limit=1&access_token=${MAPBOX_TOKEN}`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`Mapbox ${r.status}`);
        const data = await r.json();
        const feat = (data.features || [])[0];
        if (!feat || cancelled) return;
        // Use the short place name + state context if available
        const ctx = feat.context || [];
        const region = ctx.find((c) => (c.id || "").startsWith("region"));
        const country = ctx.find((c) => (c.id || "").startsWith("country"));
        const parts = [feat.text];
        if (region && region.short_code) {
          parts.push(region.short_code.replace(/^us-/i, "").toUpperCase());
        } else if (region && region.text) {
          parts.push(region.text);
        } else if (country && country.text) {
          parts.push(country.text);
        }
        setLocLabel(parts.filter(Boolean).join(", "));
      } catch (err) {
        // Reverse-geocoding is best-effort: if Mapbox returns a non-2xx or
        // the network blips, we just keep showing raw coords as fallback.
        console.warn("Reverse geocode failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [loc]);

  // Sync the input field with whatever the reverse-geocoder resolves to,
  // so the user sees a tidy "Atlanta, GA" instead of raw coordinates.
  useEffect(() => {
    if (locLabel) setLocInput(locLabel);
  }, [locLabel]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    myEstabMarkersRef.current.forEach((m) => m.remove());
    myEstabMarkersRef.current = [];
    myEstabs.forEach((e) => {
      const color = STAGE_DOT[e.pipeline_stage] || "#1A1A1A";
      const el = makePin({ color, ring: "#FFFFFF", size: 18, ariaLabel: `My ${e.name}` });
      const stage = stageMeta(e.pipeline_stage);
      const html = `
        <div style="font-family:Manrope,system-ui;min-width:220px">
          <div style="font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#737373">Your account</div>
          <div style="font-family:Fraunces,serif;font-size:18px;color:#1A1A1A;margin-top:2px">${htmlEscape(e.name)}</div>
          <div style="font-size:12px;color:#737373;margin-top:4px">${htmlEscape([e.city, e.state].filter(Boolean).join(", "))}</div>
          <span style="display:inline-block;margin-top:8px;padding:3px 10px;border-radius:999px;background:${STAGE_DOT[e.pipeline_stage]}22;color:${STAGE_DOT[e.pipeline_stage]};font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em">${stage.label}</span>
        </div>`;
      const popup = new mapboxgl.Popup({ offset: 14, closeButton: false }).setHTML(html);
      const marker = new mapboxgl.Marker(el).setLngLat([e.longitude, e.latitude]).setPopup(popup).addTo(map);
      myEstabMarkersRef.current.push(marker);
    });
  }, [myEstabs, mapReady]);

  // Render search-result markers when results change
  const renderResultMarkers = useCallback((items) => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    resultMarkersRef.current.forEach((m) => m.remove());
    resultMarkersRef.current = [];
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
    items.forEach((r) => {
      if (r.lng == null || r.lat == null) return;
      const el = makePin({ color: "#1A1A1A", ring: "#F9F7F3", size: 24, ariaLabel: r.name });
      const marker = new mapboxgl.Marker(el).setLngLat([r.lng, r.lat]).addTo(map);
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        openResultPopup(r);
      });
      resultMarkersRef.current.push(marker);
    });
    // eslint-disable-next-line
  }, [mapReady]);

  useEffect(() => {
    renderResultMarkers(results);
  }, [results, renderResultMarkers]);

  const fitMapToResults = (items, userLoc) => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!items.length && !userLoc) return;
    const bounds = new mapboxgl.LngLatBounds();
    if (userLoc) bounds.extend([userLoc.lng, userLoc.lat]);
    items.forEach((i) => {
      if (i.lng != null && i.lat != null) bounds.extend([i.lng, i.lat]);
    });
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 64, duration: 600, maxZoom: 14 });
    }
  };

  const openResultPopup = (r) => {
    const map = mapRef.current;
    if (!map) return;
    setActiveResultId(r.id);
    if (popupRef.current) popupRef.current.remove();
    const imported = importedIds.has(r.id);
    const distanceTxt = r.distance_km != null ? `${r.distance_km.toFixed(1)} km away` : "";
    const html = `
      <div style="font-family:Manrope,system-ui;min-width:240px">
        <div style="font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#737373">${htmlEscape(r.category.replace("_"," "))}</div>
        <div style="font-family:Fraunces,serif;font-size:18px;color:#1A1A1A;margin-top:2px">${htmlEscape(r.name)}</div>
        ${r.address_full ? `<div style="font-size:12px;color:#737373;margin-top:4px">${htmlEscape(r.address_full)}</div>` : ""}
        ${distanceTxt ? `<div style="font-size:11px;color:#1A1A1A;margin-top:6px;font-weight:600">${distanceTxt}</div>` : ""}
        <button id="ld-popup-add" data-id="${htmlEscape(r.id)}" ${imported ? "disabled" : ""}
          style="margin-top:10px;width:100%;padding:8px 12px;border-radius:999px;
                 border:none;background:${imported ? "#E8E4D9" : "#1A1A1A"};
                 color:${imported ? "#737373" : "#FFFFFF"};
                 font-weight:600;font-size:13px;cursor:${imported ? "default" : "pointer"};
                 display:inline-flex;align-items:center;justify-content:center;gap:6px;">
          ${imported ? "Added to list" : "+ Add to my list"}
        </button>
      </div>`;
    const popup = new mapboxgl.Popup({ offset: 18, closeButton: true, maxWidth: "280px" })
      .setLngLat([r.lng, r.lat])
      .setHTML(html)
      .addTo(map);
    popupRef.current = popup;

    // Wire up the add button inside the popup
    setTimeout(() => {
      const btn = document.getElementById("ld-popup-add");
      if (btn && !imported) {
        btn.addEventListener("click", () => importItem(r));
      }
    }, 30);

    map.flyTo({ center: [r.lng, r.lat], zoom: Math.max(map.getZoom(), 13), duration: 500 });
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation isn't available in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLoc(next);
        toast.success("Location captured");
        const map = mapRef.current;
        if (map && mapReady) {
          // Drop / move "me" marker
          if (userMarkerRef.current) userMarkerRef.current.remove();
          const el = makePin({ color: "#F28C38", ring: "#FFFFFF", size: 20, ariaLabel: "You are here" });
          el.style.boxShadow = "0 0 0 6px rgba(242,140,56,0.25), 0 2px 8px rgba(0,0,0,0.25)";
          userMarkerRef.current = new mapboxgl.Marker(el)
            .setLngLat([next.lng, next.lat])
            .setPopup(new mapboxgl.Popup({ offset: 14 }).setHTML(`<div style="font-family:Manrope;font-size:12px;color:#1A1A1A">You are here</div>`))
            .addTo(map);
          map.flyTo({ center: [next.lng, next.lat], zoom: 13, duration: 700 });
        }
      },
      (err) => {
        const map = {
          1: "Location permission denied. Enable it in your browser to discover nearby spots.",
          2: "We couldn't determine your location. Try moving to a spot with better signal.",
          3: "Location request timed out. Check your connection and try again.",
        };
        toast.error(map[err?.code] || "Unable to read your location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Forward-geocode a typed city / zip / address using Mapbox so users
  // without location-services (or searching a different area) can still
  // pin a starting point on the map.
  const geocodePlace = async () => {
    const q = locInput.trim();
    if (!q) {
      toast.message("Type a city, zip, or address — or tap the crosshair to use your current location.");
      return;
    }
    if (!MAPBOX_TOKEN) {
      toast.error("Map search isn't configured. Contact your admin.");
      return;
    }
    setGeocoding(true);
    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
        `?country=US&types=postcode,place,locality,neighborhood,address,region` +
        `&limit=1&access_token=${MAPBOX_TOKEN}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`Mapbox responded with ${r.status}`);
      const data = await r.json();
      const feat = (data.features || [])[0];
      if (!feat || !feat.center) {
        toast.error("Couldn't find that place. Try a city, zip, or address.");
        return;
      }
      const [lng, lat] = feat.center;
      const next = { lat, lng };
      setLoc(next);
      setLocLabel(feat.place_name || feat.text || q);

      const map = mapRef.current;
      if (map && mapReady) {
        if (userMarkerRef.current) userMarkerRef.current.remove();
        const el = makePin({ color: "#F28C38", ring: "#FFFFFF", size: 20, ariaLabel: "Search location" });
        el.style.boxShadow = "0 0 0 6px rgba(242,140,56,0.25), 0 2px 8px rgba(0,0,0,0.25)";
        userMarkerRef.current = new mapboxgl.Marker(el)
          .setLngLat([lng, lat])
          .setPopup(new mapboxgl.Popup({ offset: 14 }).setHTML(
            `<div style="font-family:Manrope;font-size:12px;color:#1A1A1A">${htmlEscape(feat.place_name || q)}</div>`
          ))
          .addTo(map);
        map.flyTo({ center: [lng, lat], zoom: 12, duration: 700 });
      }
      toast.success(`Searching near ${feat.text || q}`);
    } catch (e) {
      toast.error(e.message || "Couldn't find that place.");
    } finally {
      setGeocoding(false);
    }
  };

  const search = async () => {
    if (!loc) { toast.error("Pick a location first — current, city, or zip."); return; }
    setBusy(true);
    setResults([]);
    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/category/${encodeURIComponent(category)}?proximity=${loc.lng},${loc.lat}&limit=25&access_token=${MAPBOX_TOKEN}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`Mapbox responded with ${r.status}`);
      const data = await r.json();
      const features = data.features || [];
      const items = features.map((f) => {
        const coords = f.geometry?.coordinates || [];
        const props = f.properties || {};
        const distance =
          coords.length === 2 ? haversineMi([loc.lng, loc.lat], coords) : null;
        return {
          id: props.mapbox_id || `${f.id || props.name}-${coords.join(",")}`,
          name: props.name || "Unnamed",
          address_full: props.full_address || props.place_formatted || props.address || "",
          category,
          lng: coords[0],
          lat: coords[1],
          distance_mi: distance,
        };
      })
      .filter((x) => x.distance_mi == null || x.distance_mi <= radius)
      .sort((a, b) => (a.distance_mi ?? 9999) - (b.distance_mi ?? 9999));
      setResults(items);
      fitMapToResults(items, loc);
      if (items.length === 0) toast.message("No results in that radius. Try widening it.");
    } catch (e) {
      toast.error(e.message || "Search failed");
    } finally {
      setBusy(false);
    }
  };

  const importItem = async (item) => {
    try {
      const parsed = parseAddress(item.address_full);
      const typeForDb =
        item.category === "bar" ? "bar"
        : item.category === "liquor_store" ? "liquor_store"
        : "restaurant";
      const payload = {
        name: item.name,
        type: typeForDb,
        ...parsed,
        latitude: item.lat,
        longitude: item.lng,
        pipeline_stage: "lead",
        brand_ids: [],
        contact_name: "",
        contact_phone: "",
        contact_email: "",
        notes: `Imported from Mapbox Discover (${item.distance_mi?.toFixed(2)} mi away).`,
      };
      const { data: created } = await api.post("/establishments", payload);
      toast.success(`Added "${item.name}" as Lead`);
      setImportedIds((s) => new Set([...s, item.id]));
      setMyEstabs((arr) => [...arr, created]);
      if (popupRef.current) popupRef.current.remove();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        testid="search-header"
        title="Discover nearby"
        subtitle="See nearby restaurants, bars, and liquor stores on the map — and pin them to your book in one tap."
      />

      <div className="card p-5 mb-4">
        <div className="grid md:grid-cols-[1fr,auto] gap-3 items-end">
          <div className="grid sm:grid-cols-[1fr,1fr,120px] gap-3">
            <div>
              <div className="label mb-1">Your location</div>
              <div className="relative">
                <input
                  type="text"
                  value={locInput}
                  onChange={(e) => setLocInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      geocodePlace();
                    }
                  }}
                  placeholder="City, zip, or address"
                  className="input-base pr-11"
                  data-testid="location-input"
                  aria-label="Search location by city, zip code, or address"
                />
                <button
                  type="button"
                  onClick={getLocation}
                  disabled={geocoding}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-brand-ink2 hover:bg-brand-muted transition-colors disabled:opacity-50"
                  title="Use my current location"
                  data-testid="get-location-button"
                >
                  <Crosshair size={16} />
                </button>
              </div>
            </div>
            <div>
              <div className="label mb-1">Category</div>
              <div className="flex gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = category === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      data-testid={`cat-${c.value}`}
                      onClick={() => setCategory(c.value)}
                      className={`flex-1 px-3 py-2.5 rounded-xl border transition-all flex items-center justify-center gap-1.5 text-sm font-medium ${active ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-border text-brand-ink hover:bg-brand-muted"}`}
                    >
                      <Icon size={14} /> <span className="hidden sm:inline">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label mb-1">Radius</div>
              <select
                data-testid="radius-select"
                className="input-base"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
              >
                {RADII.map((r) => (
                  <option key={r.mi} value={r.mi}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={search}
            disabled={!loc || busy}
            className="btn-primary"
            data-testid="discover-button"
          >
            <Compass size={16} /> {busy ? "Searching…" : "Discover"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-4">
        {/* Map */}
        <div className="card p-2 overflow-hidden">
          <div
            ref={mapContainer}
            data-testid="discover-map"
            className="relative w-full rounded-xl overflow-hidden bg-brand-muted h-[380px] sm:h-[460px] lg:h-[560px]"
          >
            {mapError && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center" data-testid="map-error">
                <div className="max-w-xs">
                  <div className="label mb-2">Map unavailable</div>
                  <p className="text-sm text-brand-ink2">{mapError}</p>
                </div>
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 px-2 py-3 text-xs text-brand-mutedFg" data-testid="map-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F28C38", boxShadow: "0 0 0 3px rgba(242,140,56,0.25)" }} /> You
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-brand-ink ring-2 ring-brand-bg" /> Nearby
            </span>
            <span className="w-px h-3 bg-brand-border" />
            <span className="text-brand-mutedFg">Your accounts:</span>
            {STAGES.map((s) => (
              <span key={s.value} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_DOT[s.value] }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* List */}
        <div>
          {busy ? (
            <Spinner label="Searching nearby…" />
          ) : results.length === 0 ? (
            <EmptyState
              testid="empty-search"
              icon={MapPinned}
              title={loc ? "No results yet" : "Get started"}
              body={loc ? "Hit Discover to pull up nearby spots." : "Allow location access so we can find restaurants, bars, and liquor stores near you."}
            />
          ) : (
            <div className="flex flex-col gap-3 max-h-[380px] sm:max-h-[460px] lg:max-h-[560px] overflow-y-auto pr-1" data-testid="search-results">
              {results.map((r) => {
                const imported = importedIds.has(r.id);
                const Icon = r.category === "bar" ? Wine : r.category === "liquor_store" ? Store : Utensils;
                const active = activeResultId === r.id;
                return (
                  <div
                    key={r.id}
                    className={`card p-4 transition-all cursor-pointer ${active ? "ring-2 ring-brand-ink" : ""}`}
                    data-testid={`search-result-${r.id}`}
                    onClick={() => openResultPopup(r)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand-muted flex items-center justify-center text-brand-ink shrink-0">
                        <Icon size={15} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base text-brand-ink leading-tight">{r.name}</div>
                        <div className="text-[11px] text-brand-mutedFg capitalize">{r.category.replace("_", " ")}</div>
                        {r.address_full && (
                          <div className="text-xs text-brand-mutedFg mt-1 line-clamp-2">{r.address_full}</div>
                        )}
                      </div>
                      {r.distance_mi != null && (
                        <span className="badge bg-brand-muted text-brand-ink shrink-0">{r.distance_mi.toFixed(1)} mi</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); importItem(r); }}
                      disabled={imported}
                      className={`mt-3 w-full ${imported ? "btn-outline" : "btn-primary"} py-2 text-sm`}
                      data-testid={`import-${r.id}`}
                    >
                      {imported ? "Added to list" : (<><Plus size={14} /> Add to my list</>)}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
